import React from 'react';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import checklistIcon from '../../../../assets/images/checklist-retina.png';
import speechBubblesIcon from '../../../../assets/images/speech-bubbles.png';
import earnIcon from '../../../../assets/images/earn.png';
import pShieldIcon from '../../../../assets/images/p-shield-retina.png';
import shippingBoxIcon from '../../../../assets/images/shipping-box-retina.png';
import questionMarkIcon from '../../../../assets/images/questionmark-retina.png';
import mouseIcon from '../../../../assets/images/mouse-retina.png';
import scoopIcon from '../../../../assets/images/scoop-retina.png';
import podcastIcon from '../../../../assets/images/podcast-retina.png';
import FeatureFlagsContext from '../FeatureFlagsContext';
import {
  CircularImageAndTextBox,
  SpeechBubble,
  IconAndCallToAction,
} from 'components/shared';

const EnrollInDirectDispense = () => (
  <Button
    color="primary"
    variant="outlined"
    sx={{ padding: '0.8125rem 2.25rem' }}
  >
    <a href="https://www.platinumperformance.com/veterinarian/directdispense.html">
      Enroll in Direct Dispense<sup>&reg;</sup>
    </a>
  </Button>
);

export default function ResellerFinish({
  signUrl,
  country,
  platinumEcomBaseUrl,
  platinumCanadaEcomBaseUrl,
}) {
  const theme = useTheme();
  const featureFlags = React.useContext(FeatureFlagsContext);

  if (country === 'Canada') {
    return (window.location.href = `${platinumCanadaEcomBaseUrl}/hub-thank-you.html`);

    return null;
  } else if (
    !featureFlags.showResellerSignatureLink &&
    country === 'United States'
  ) {
    window.location.href = `${platinumEcomBaseUrl}/hub-thank-you.html`;

    return null;
  }

  return (
    <>
      <Stack
        useFlexGap
        spacing={2}
        textAlign="center"
        backgroundColor={theme.palette.success.light}
        alignItems="center"
        padding="2rem 1.875rem 2.125rem"
        marginTop="2.25rem"
        // I've only seen this font-family/font-weight combo in E-commerce's Thank You page
        // The default seems to be 'Whitney Book' and a font weight of 400
        sx={{
          '& p': {
            fontFamily: 'Whitney Medium',
            fontWeight: 500,
          },
        }}
      >
        <Typography variant="h1" color={theme.palette.success.main}>
          Thank You for Registering. Just One More Step!
        </Typography>
        <Typography variant="p" color={theme.palette.success.dark}>
          Next, sign your
        </Typography>
        <Button color="primary" variant="contained">
          <a href={signUrl}>Veterinary Resellers Agreement</a>
        </Button>
        <Typography
          maxWidth="35rem"
          variant="p"
          color={theme.palette.success.dark}
        >
          ...to secure wholesale pricing and access to the Platinum Performance®
          veterinary portal containing a vast collection of veterinary
          resources.
        </Typography>
      </Stack>
      <Stack
        alignItems="center"
        useFlexGap
        sx={{
          // Add padding to all direct children unless it's an hr element, which we're using as a divider
          '& > *:not(hr)': {
            padding: '1rem 1rem 2rem',
            maxWidth: '82.5rem',
          },
        }}
        spacing={2}
        divider={<hr />}
      >
        <Stack
          sx={{
            // In the E-commmerce site this one doesn't match the other children
            // This padding supplements the default padding shared by the children
            '&:not(hr)': {
              paddingTop: '2.875rem',
            },
          }}
          useFlexGap
          spacing={2.25}
          textAlign="center"
          alignItems="center"
        >
          <Typography
            variant="h1"
            textAlign="center"
            sectionComponentSize="large"
          >
            Finish Your Enrollment to Take Full Advantage
          </Typography>
          <Typography
            variant="h2"
            textAlign="center"
            sectionComponentSize="large"
          >
            See Why Thousands of Practices Rely On Direct Dispense®
          </Typography>
          <Typography variant="p">
            Direct Dispense® is a simple, no-strings program that allows you to
            recommend Platinum Performance® formulas to your clients and let us
            take it from there. We deliver exceptional service to your clients
            while handling billing and shipping, and you receive a monthly
            report and earnings from every order.
          </Typography>
          <Typography variant="p">
            No more inventory management. No fees or commitments. We'll take
            care of everything.
          </Typography>
          <EnrollInDirectDispense />
        </Stack>
        <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={3}>
          <Stack
            useFlexGap
            alignItems="center"
            textAlign="center"
            spacing={1.5}
          >
            <img src={checklistIcon} width="80px" />
            <Typography variant="h2">1. Enroll</Typography>
            <p>
              Online of over the phone. Your welcome kit will arrive promptly
              with Direct Dispensee<sup>&reg;</sup> prescription pads.
            </p>
          </Stack>
          <Stack
            useFlexGap
            alignItems="center"
            textAlign="center"
            spacing={1.5}
          >
            <img src={speechBubblesIcon} width="80px" />
            <Typography variant="h2">2. Recommend</Typography>
            <p>
              Recommend formulas to help your patients. Have clients call
              Platinum to place orders.
            </p>
          </Stack>
          <Stack
            useFlexGap
            alignItems="center"
            textAlign="center"
            spacing={1.5}
          >
            <img src={earnIcon} width="80px" />
            <Typography variant="h2">3. Earn</Typography>
            <p>
              Platinum handles orders, billing and fulfillment. Your practice
              receives 20% of gross order value every month.
            </p>
          </Stack>
        </Stack>
        <Stack useFlexGap spacing={3} sx={{ '&&': { paddingBottom: '4rem' } }}>
          <Typography variant="h1">
            Our Experienced Platinum Advisors
          </Typography>
          <Stack spacing={5.5}>
            <CircularImageAndTextBox
              imgSrc={pShieldIcon}
              backgroundColor="#4c7ba8"
            >
              Offer exceptional personal service and answer nutrition questions
              with a high level of expertise
            </CircularImageAndTextBox>
            <CircularImageAndTextBox
              imgSrc={shippingBoxIcon}
              backgroundColor="#6c8cb5"
            >
              Place your client's order, charge their card retail price and
              fulfill a timely shipment of products
            </CircularImageAndTextBox>
            <CircularImageAndTextBox
              imgSrc={questionMarkIcon}
              backgroundColor="#8aa1c2"
            >
              Place your client's order, charge their card retail price and
              fulfill a timely shipment of products
            </CircularImageAndTextBox>
          </Stack>
        </Stack>
        <Stack
          useFlexGap
          spacing={1}
          sx={{ textAlign: 'center', '&&': { paddingBottom: '4rem' } }}
          alignItems="center"
        >
          <Typography variant="h1">
            Easy for Your Clients, Profitable for Your Practice
          </Typography>
          <Typography variant="h2">
            Frequently Asked Questions ... Answered
          </Typography>
          <Stack
            spacing={5.5}
            direction={{ xs: 'column', sm: 'row' }}
            sx={{ paddingTop: '1rem' }}
          >
            <SpeechBubble backgroundColor="#eff1f5">
              <Typography variant="h3">Do I Stock Inventory?</Typography>
              <p>
                No. Platinum formulas ship quickly from Platinum and arrive
                directly to your clients. They will receive free shipping on
                their first order. If you would like to have products on hand,
                you are welcome to but not required.
              </p>
            </SpeechBubble>
            <SpeechBubble backgroundColor="#e3e6ee">
              <Typography variant="h3">How Do You Know My Clients?</Typography>
              <p>
                Our Platinum Advisors ask, “How did you hear about us” on every
                order. If you are enrolled in Direct Dispense® and your client
                places an order, their account is connected to yours, and all
                orders go toward your DD earnings.
              </p>
            </SpeechBubble>
            <SpeechBubble backgroundColor="#d6dce8">
              <Typography variant="h3">What's My Commitment?</Typography>
              <p>
                Recommend the products that will support your patients'
                individual health needs. Each month, you will receive a report
                showing client orders and your Direct Dispense® earnings,
                totaling 20% of the gross order value.
              </p>
            </SpeechBubble>
          </Stack>
          <blockquote
            style={{
              // Color was taken directly from E-commerce site's blockquote
              color: 'rgb(39, 39, 39)',
              fontSize: '1.375rem',
              lineHeight: 1.5,
              textAlign: 'center',
              padding: 0,
              marginTop: '4rem',
              marginBottom: '1.75rem',
            }}
          >
            “Nutrition can be powerful, no matter what type of case you are
            dealing with; it's a standard of care. Proper nutrition is an
            important part of practicing good medicine.”
            <br />
            <small style={{ fontSize: '1.25rem', lineHeight: '28px' }}>
              <strong>— Doug Herthel, DVM (1946-2018)</strong>
              ,
              <br />
              Founder, Platinum Performance®
            </small>
          </blockquote>
          <EnrollInDirectDispense />
        </Stack>
        <Stack spacing={3}>
          <Typography variant="h1">
            Additional Veterinary Partner Resources
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
            <IconAndCallToAction imgSrc={mouseIcon}>
              <Typography variant="h3">RACE-Approved CE</Typography>
              <Typography variant="p">
                We offer an extensive and ever-expanding library of on-demand,
                RACE-approved CE webinars featuring leading veterinarians and
                research discussing pertinent topics to your practice.
              </Typography>
              <Button variant="outlined">
                <a href="https://www.platinumperformance.com/on-demand-ce-lectures-loggedin.html">
                  Watch Now
                </a>
              </Button>
            </IconAndCallToAction>
            <IconAndCallToAction imgSrc={scoopIcon}>
              <Typography variant="h3">Platinum Protocols</Typography>
              <Typography variant="p">
                Our formulas can truly make an impact in a patient's health,
                performance and recovery as a strong complimentary therapy. See
                our in-depth protocols that detail exactly how to use Platinum
                formulas in most major equine conditions.
              </Typography>
              <Button variant="outlined">
                <a href="https://www.platinumperformance.com/protocols.html">
                  Learn More
                </a>
              </Button>
            </IconAndCallToAction>
            <IconAndCallToAction imgSrc={podcastIcon}>
              <Typography variant="h3">It Starts Within Podcast</Typography>
              <Typography variant="p">
                Subscribe to the Platinum Performance® Podcast and join us as we
                dive deep into inspiring stories and the latest advancements in
                equine health, treatments and comebacks.
              </Typography>
              <Button variant="outlined">
                <a href="https://www.platinumperformance.com/podcasts.html">
                  Listen Now
                </a>
              </Button>
            </IconAndCallToAction>
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}
